import type { ConnectivitySPOF } from '../../types';

interface ConnectivitySpofTabProps {
  spof: ConnectivitySPOF | undefined;
}

const severityColors: Record<string, string> = { critical: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };
const severityLabels: Record<string, string> = { critical: 'Критично', warning: 'Внимание', info: 'Инфо' };

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="rounded-lg border border-slate-800/60 bg-slate-950/40 p-4 flex-1 min-w-[120px] shadow-sm">
      <div className="text-[10px] font-semibold font-mono text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`mt-1 text-xl font-bold font-mono ${color || 'text-slate-100'}`}>{value}</div>
    </div>
  );
}

function IssueRow({ issue }: { issue: ConnectivitySPOF['issues'][0] }) {
  return (
    <tr className="hover:bg-slate-800/20 transition-colors">
      <td className="py-2.5 px-3">
        <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: severityColors[issue.severity] }} />
        <span className="text-slate-200">{severityLabels[issue.severity]}</span>
      </td>
      <td className="py-2.5 px-3 text-xs text-slate-400 font-mono">{issue.category}</td>
      <td className="py-2.5 px-3 text-xs text-slate-200">{issue.description}</td>
      <td className="py-2.5 px-3 text-xs text-slate-400 font-mono">
        {issue.affected.length > 0 ? issue.affected.slice(0, 3).join(', ') : '—'}
      </td>
    </tr>
  );
}

export function ConnectivitySpofTab({ spof }: ConnectivitySpofTabProps) {
  return (
    <div>
      <h2 className="text-base font-semibold font-mono text-slate-100 mb-4">Анализ точек отказа</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Всего устройств" value={spof?.summary.total_devices ?? 0} />
        <StatCard label="Отключённые" value={spof?.summary.disconnected_devices ?? 0} color="text-rose-400" />
        <StatCard label="Одноподкл." value={spof?.summary.single_connection_devices ?? 0} color="text-amber-400" />
        <StatCard label="Компоненты" value={spof?.summary.connected_components ?? 0} />
        <StatCard
          label="Проблем"
          value={spof?.issues.length ?? 0}
          color={spof && spof.issues.some((i) => i.severity === 'critical') ? 'text-rose-400' : 'text-emerald-400'}
        />
      </div>
      {spof && spof.issues.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-800/60">
          <table className="w-full text-xs font-mono">
            <thead className="bg-slate-950/60 border-b border-slate-800/60">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium text-slate-400">Уровень</th>
                <th className="text-left px-3 py-2.5 font-medium text-slate-400">Категория</th>
                <th className="text-left px-3 py-2.5 font-medium text-slate-400">Описание</th>
                <th className="text-left px-3 py-2.5 font-medium text-slate-400">Затронутые</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/30">
              {spof.issues.map((i, idx) => (
                <IssueRow key={idx} issue={i} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-emerald-400 font-mono text-xs">Проблем не обнаружено</div>
      )}
    </div>
  );
}
